import {
  Box,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
  Typography,
} from "@mui/material";
import FeedIcon from "@mui/icons-material/Feed";
import {
  Analytics,
  AttachMoney,
  Dashboard,
  Person,
  School,
  Task,
  Receipt,
  History as HistoryIcon,
  EmailOutlined as EmailTemplateIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { Link, useLocation } from "react-router-dom";
import { useAppTheme } from "../../context/ThemeContext";

const drawerWidth = 240;

const StyledLink = styled(Link)`
  text-decoration: none;
  width: 100%;
  color: inherit;
  &:focus,
  &:hover,
  &:visited,
  &:link,
  &:active {
    text-decoration: none;
  }
`;

const routeLinks = [
  { name: "Dashboard", path: "/", icon: <Dashboard /> },
  { name: "Users", path: "/users", icon: <Person />, admin: true },
  { name: "Audit Log", path: "/audit", icon: <HistoryIcon />, admin: true },
  { name: "Email Templates", path: "/email-templates", icon: <EmailTemplateIcon />, admin: true },
  { name: "Tasks", path: "/tasks", icon: <Task /> },
  { name: "Submissions", path: "/submissions", icon: <FeedIcon /> },
  { name: "Students", path: "/students", icon: <School /> },
  { name: "Transactions", path: "/transactions", icon: <AttachMoney /> },
  { name: "Analytics", path: "/analytics", icon: <Analytics /> },
  { name: "Invoices", path: "/invoices", icon: <Receipt /> },
];

function NavItem({
  link,
  active,
  isNewTheme,
  accentColor,
}: {
  link: typeof routeLinks[0];
  active: boolean;
  isNewTheme: boolean;
  accentColor: string;
}) {
  return (
    <ListItem disablePadding>
      <StyledLink to={link.path}>
        <ListItemButton
          selected={!isNewTheme && active}
          sx={
            isNewTheme
              ? {
                  borderRadius: 0,
                  py: 1.2,
                  transition: "all 0.15s ease",
                  backgroundColor: active ? `${accentColor}26` : "transparent",
                  borderLeft: active ? `3px solid ${accentColor}` : "3px solid transparent",
                  "&:hover": {
                    backgroundColor: active ? `${accentColor}33` : "#F8FAFC",
                  },
                }
              : {}
          }>
          <ListItemIcon
            sx={
              isNewTheme
                ? {
                    color: active ? accentColor : "#475569",
                    minWidth: 40,
                    transition: "color 0.15s ease",
                  }
                : {}
            }>
            {link.icon}
          </ListItemIcon>
          <ListItemText
            primary={link.name}
            primaryTypographyProps={
              isNewTheme
                ? {
                    fontWeight: active ? 600 : 400,
                    fontSize: "0.875rem",
                    color: active ? "#0F172A" : "#475569",
                    letterSpacing: "0.01em",
                  }
                : {}
            }
          />
        </ListItemButton>
      </StyledLink>
    </ListItem>
  );
}

function Sidebar({ sidebar }: { sidebar: boolean }) {
  const location = useLocation();
  const { isNewTheme, accentColor } = useAppTheme();
  const role = useSelector((state: RootState) => state.user.role);
  const permissions = useSelector((state: RootState) => state.user.permissions);

  const allowedResources = permissions.map((res) => {
    if (res.actions.includes("READ")) return res.resource;
  });

  const allowedLink = routeLinks.filter((link) => {
    if (role === "admin") return true;
    if (link.name === "Dashboard") return true;
    if (allowedResources.includes(link.name.toLowerCase())) return true;
  });

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <Drawer
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          top: 64,
          height: "calc(100% - 64px)",
          borderRadius: 0,
          ...(isNewTheme
            ? {
                backgroundColor: "#ffffff",
                borderRight: "1px solid #E2E8F0",
                boxShadow: "none",
              }
            : {
                background: "#fff",
                borderRight: "1px solid rgba(0,0,0,0.12)",
              }),
        },
      }}
      variant="persistent"
      anchor="left"
      open={sidebar}>
      <List sx={{ pt: 1.5 }}>
        {allowedLink
          .filter((i) => !i.admin)
          .map((link) => (
            <NavItem
              key={link.name}
              link={link}
              active={isActive(link.path)}
              isNewTheme={isNewTheme}
              accentColor={accentColor}
            />
          ))}
      </List>

      <Divider
        sx={
          isNewTheme
            ? { borderColor: "#E2E8F0", mx: 2 }
            : {}
        }
      />

      {role === "admin" && (
        <>
          <Typography
            variant="caption"
            sx={{
              px: 2.5,
              pt: 1,
              pb: 0.25,
              display: "block",
              letterSpacing: "0.12em",
              fontWeight: 700,
              fontSize: "0.6rem",
              textTransform: "uppercase",
              color: isNewTheme ? "#94A3B8" : "text.disabled",
            }}>
            Admin
          </Typography>
          <List sx={{ pt: 0 }}>
            {routeLinks
              .filter((i) => i.admin)
              .map((link) => (
                <NavItem
                  key={link.name}
                  link={link}
                  active={isActive(link.path)}
                  isNewTheme={isNewTheme}
                  accentColor={accentColor}
                />
              ))}
          </List>
        </>
      )}
    </Drawer>
  );
}

export default Sidebar;
